import { escapeHtml } from '../../../utils/escapeHtml';
import React, { useImperativeHandle, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import './PieChart.scss';
import { EChartsOption } from 'echarts';
import { OTHERS_COLOR, SELECTED_BRAND_COLOR } from '../../../utils/utils';
import { getChartThemeColors, getFirstSeriesPalette } from '../../../utils/chartTheme';
import { useTheme } from '../../../contexts/theme/ThemeContext';
import { t } from 'i18next';
import { PieChartData } from '../../../models/common/PieChartData';

interface PieChartProps {
  chartData: PieChartData[];
  colorPalette?: string[];
  chartSize?: string | null;
  selectedBrand?: string;
  showHalf?: boolean;
  size?: string;
  legendRef?: React.RefObject<HTMLElement>;
  chartRef?: React.RefObject<ReactECharts>;
  /** Opt-in: capitalize brand names in the tooltip (display only). */
  capitalizeBrands?: boolean;
  /** Opt-in: draw slice names around the chart (raporty czyta się bez najeżdżania myszą). */
  showLabels?: boolean;
}

const PieChart: React.FC<PieChartProps> = ({
  chartData,
  colorPalette = getFirstSeriesPalette(),
  chartSize,
  selectedBrand,
  showHalf = false,
  size,
  legendRef,
  chartRef,
  capitalizeBrands = false,
  showLabels = false,
}) => {
  const eChartsRef = useRef<ReactECharts>(null);
  const { theme } = useTheme();
  // Bez listy zależności: wykres montuje się od nowa przy zmianie motywu (`key={theme}`), a uchwyt z `[]`
  // zostawał przy starym, odmontowanym wykresie — najechanie na legendę sięgało do nieistniejącego DOM.
  useImperativeHandle(chartRef, () => eChartsRef.current!);

  const getPieSeries = (data: PieChartData[]): Object[] => {
    const minThreshold = 1;
    const total = data.reduce((sum, item) => sum + item.value, 0);

    const getRadius = () => {
      // Etykiety wokół koła potrzebują marginesu na wynoszące je linie.
      if (showLabels) {
        return ['45%', '68%'];
      }

      if (showHalf) {
        if (size === 'SM') {
          return ['90%', '130%'];
        }

        return ['135%', '165%'];
      }

      if (size === 'SM') {
        return ['70%', '90%'];
      }

      return ['75%', '97%'];
    };

    const adjustedData = data.map((item) => {
      const adjustedValue = item.value > 0 ? Math.max(item.value, (total * minThreshold) / 100) : 0;
      return { ...item, adjustedValue };
    });

    return [
      {
        type: 'pie',
        data: adjustedData.map((item) => ({
          name: item.name,
          value: item.adjustedValue,
        })),
        radius: getRadius(),
        avoidLabelOverlap: true,
        startAngle: 180,
        ...(showHalf
          ? {
              center: ['50%', '90%'],
              endAngle: 360,
            }
          : {}),
        itemStyle: {
          borderRadius: 1,
          borderWidth: 1,
          borderColor: getChartThemeColors().pieBorder,
        },
        label: showLabels
          ? {
              show: true,
              // Wartość bierzemy z oryginalnych danych — `adjustedValue` podnosi wycinki poniżej 1%,
              // żeby dało się je kliknąć, i podpisana nim etykieta kłamałaby.
              formatter: (params: any) =>
                `${params.name}\n${data.find((item) => item.name === params.name)?.value ?? 0}%`,
              color: getChartThemeColors().tooltipText,
              fontSize: 11,
              lineHeight: 15,
            }
          : { show: false },
        labelLine: showLabels ? { length: 10, length2: 12 } : undefined,
      },
    ];
  };

  const getPieChartPalette = (): string[] => {
    const updatedPalette = [...colorPalette];
    const index = chartData.findIndex((d) => d.name === selectedBrand);
    const otherIndex = chartData.findIndex((d) => d.name === t('others'));

    if (index !== -1) updatedPalette[index] = SELECTED_BRAND_COLOR;
    if (otherIndex !== -1) updatedPalette[otherIndex] = OTHERS_COLOR;

    return updatedPalette;
  };

  const formatTooltip = (params: any): string => {
    const originalData = chartData.find((d) => d.name === params.data.name);
    const value = originalData?.value ? `${originalData.value}%` : '-';

    return `
      <div class="chart-tooltip__content chart-tooltip__content--pie">
        <div class="chart-tooltip__row">
          <div class="chart-tooltip__marker">${params.marker}</div>
          <div class="chart-tooltip__text"><span>${escapeHtml(params.data.name)}</span></div>
          <div>${value}</div>
        </div>
      </div>
    `;
  };

  const updateLegendHighlight = (name?: string) => {
    const legendItems = legendRef?.current?.querySelectorAll('[data-name]');
    legendItems?.forEach((el: any) => {
      const element = el as HTMLElement;
      element.classList.toggle('legend-tag--active', element.dataset.name === name);
    });
  };

  const chartColors = getChartThemeColors();

  const options: EChartsOption = {
    legend: { show: false },
    tooltip: {
      borderRadius: 8,
      padding: [5, 10],
      borderColor: chartColors.tooltipBorder,
      backgroundColor: chartColors.tooltipBg,
      appendToBody: true,
      ...(capitalizeBrands ? { className: 'capitalize' } : {}),
      textStyle: {
        color: chartColors.tooltipText,
        fontSize: 12,
        lineHeight: 28,
        fontWeight: 600,
      },
      trigger: 'item',
      position: (point, _, __, ___, size) => {
        const [x, y] = point;
        const tooltipWidth = size.contentSize[0];
        const posX = x + 10;
        return [posX + tooltipWidth > size.viewSize[0] ? x - tooltipWidth + 180 : posX, y];
      },
      formatter: formatTooltip,
    },
    xAxis: undefined,
    yAxis: undefined,
    color: getPieChartPalette(),
    series: getPieSeries(chartData),
  };

  const getHeight = () => {
    if (showLabels) {
      return 320;
    }

    if (showHalf) {
      if (size === 'SM') {
        return 35;
      }

      return 100;
    }

    if (size === 'SM') {
      return 170;
    }

    if (size === 'XXL') {
      return 263;
    }

    return 183;
  };

  return (
    <div className={`${chartSize && 'chart--sm'} chart`}>
      <div className="chart__box">
        <ReactECharts
          key={theme}
          ref={eChartsRef}
          option={options}
          style={{ height: getHeight() + 'px', width: '100%' }}
          onEvents={{
            mouseover: (params: any) => {
              if (params.componentType === 'series' && params.seriesType === 'pie') {
                updateLegendHighlight(params.name);
              }
            },
            mouseout: () => updateLegendHighlight(),
          }}
        />
      </div>
    </div>
  );
};

export default PieChart;
